import React, { useState, useMemo } from "react";
import styles from "./ConfigureShortcutModal.module.scss";
import { BaseModal, ModalHeader } from "@whatslly/ui-components/modals";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { observer } from "mobx-react-lite";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";

interface ConfigureShortcutModalProps extends IModalInstance<{ shortcutUrl: string }> {
  shortcutUrl?: string;
}

const SLACK_SHORTCUT_REGEX = /^https:\/\/slack\.com\/shortcuts\/[A-Za-z0-9]+\/[a-f0-9]+$/;

export const ConfigureShortcutModal: React.FC<ConfigureShortcutModalProps> = observer(({ closeModal, shortcutUrl }) => {
  const { t } = useTranslation();
  const [shortcutUrlState, setShortcutUrl] = useState<string>(shortcutUrl ?? "");
  const [touched, setTouched] = useState(false);

  const isValidUrl = useMemo(() => {
    if (!shortcutUrlState?.trim()) return false;
    return SLACK_SHORTCUT_REGEX.test(shortcutUrlState.trim());
  }, [shortcutUrlState]);

  const showError = touched && shortcutUrlState?.trim() && !isValidUrl;

  const handleSubmit = () => {
    if (isValidUrl) {
      closeModal({ shortcutUrl: shortcutUrlState.trim() });
    }
  };

  return (
    <BaseModal
      headerClassName={styles.header}
      header={<ModalHeader closeModal={closeModal}>{t("slack_settings_page.configure_shortcut_modal.title")}</ModalHeader>}
      closeModal={closeModal}
      className={styles.modal}
      bodyClassName={styles.body}
      testId="configure-shortcut-modal"
    >
      <div className={styles.content}>
        <div className={styles.field}>
          <label>{t("slack_settings_page.configure_shortcut_modal.shortcut_url_label")}</label>
          <TextInput
            autoFocus
            value={shortcutUrl}
            onChange={(e) => setShortcutUrl(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="https://slack.com/shortcuts/Ft09RMK3E0GH/b9eb4c9a1b62dd49866da2898592fb08"
            data-testid="shortcut-url-input"
            paddingInline="small"
          />
          {showError && <span className={styles.error}>{t("slack_settings_page.configure_shortcut_modal.invalid_url")}</span>}
        </div>
      </div>

      <ModalButtons
        className={styles.buttons}
        isSubmitDisabled={!isValidUrl}
        testId="configure-shortcut-submit"
        submitText={t("slack_settings_page.configure_shortcut_modal.save")}
        onSubmit={handleSubmit}
        onCancel={() => closeModal()}
      />
    </BaseModal>
  );
});
